import { type DeliveryMethod } from '@alto/deliver_api/types/delivery_methods/v1/delivery_method';
import { type ModeType } from '@alto/deliver_api/types/delivery_methods/v1/mode_type';
import { isFuzeRxBrand } from '@alto/design-system';
import { getImage } from '~shared/assets/images/getImage';
import { getImageAltText } from '~shared/assets/images/getImageAltText';
import { type APIOrderMethod__DEPRECATED, type APIOrderStatus } from '~shared/features/delivery-info/types';
import { SHIPMENT_STATUS_MAP, type StatusMap } from '~shared/features/shipments/constants';

export const COURIER_DELIVERY_METHODS = ['courier', 'onfleet'];

export const isMail = (mode: ModeType | null | undefined) => {
  return mode === 'MAIL';
};

export const isPickup = (mode: ModeType | null | undefined) => {
  return mode === 'PICKUP';
};

export const isCourier = (mode: ModeType | null | undefined) => {
  return mode === 'COURIER';
};

// Please use isCourier by passing in the delivery mode: ModeType instead
export const isCourier__DEPRECATED = (method: APIOrderMethod__DEPRECATED | undefined | null) => {
  return COURIER_DELIVERY_METHODS.includes(method ?? '');
};

// add null and undefined to set typing to satisfy TypeScript
export const STATUSES_TO_SHOW_MAIL_CARRIER = new Set<APIOrderStatus | null | undefined>([
  'delivered',
  'failed',
  'returned',
  'shipped',
]);

export const getMailCopy = (method: DeliveryMethod | null | undefined, status: APIOrderStatus | null | undefined) => {
  if (STATUSES_TO_SHOW_MAIL_CARRIER.has(status)) {
    const statusText = status === 'delivered' ? 'Delivered' : 'Ships';
    return `${statusText} via ${method?.patient_facing_name || 'mail'}`;
  }

  return 'Ships via mail';
};

export const getMailLogo = (method: DeliveryMethod | null | undefined, status: APIOrderStatus | null | undefined) => {
  if (!isFuzeRxBrand() && STATUSES_TO_SHOW_MAIL_CARRIER.has(status)) {
    return method?.mail_carrier_struct?.logo_path;
  }

  return getImage('mail');
};

export type orderHelperProps = {
  method: DeliveryMethod | undefined | null;
  status: APIOrderStatus | undefined | null;
  failedPaymentMethods?: string;
  shipmentStatusMap?: StatusMap;
};

export const getOrderStatusTitle = ({ method, status, failedPaymentMethods, shipmentStatusMap }: orderHelperProps) => {
  if (!status) {
    return '';
  }
  const statusMap = shipmentStatusMap || SHIPMENT_STATUS_MAP;
  if (failedPaymentMethods) {
    return 'Update your payment method';
  }
  if (isMail(method?.mode)) {
    return statusMap[status]?.mailInfoText || '';
  }
  if (isPickup(method?.mode)) {
    return status === 'shipped' ? 'Your order is ready' : statusMap[status]?.pickupInfoText || '';
  }
  // pickup and courier have same title in processing state
  return statusMap[status]?.infoText || '';
};

export const getIllustration = ({ method, status }: orderHelperProps) => {
  // show delivery logo throughout pickup lifecycle
  if (isPickup(method?.mode)) {
    return getImage('pickup');
  }
  // show mail logo throughout lifecycle
  if (isMail(method?.mode)) {
    return getMailLogo(method, status);
  }
  // show calendar for all processing stages
  if (status === 'processing' || status === 'awaiting_packing' || status === 'packaged') {
    return getImage('calendarAngled');
  }
  // show the courier for the shipped stage (mail and pickup returned above)
  if (status === 'shipped') {
    return getImage('shipmentCourier');
  }
  // show service fee (delivery woman) for delivered stage
  if (status === 'delivered') {
    return getImage('deliveryWoman');
  }
};

export const getAccessibilityLabel = ({ method, status }: orderHelperProps) => {
  if (isPickup(method?.mode)) {
    return getImageAltText('pickup');
  }
  if (isMail(method?.mode)) {
    return !isFuzeRxBrand() && STATUSES_TO_SHOW_MAIL_CARRIER.has(status)
      ? `Illustration of ${method?.patient_facing_name || 'mail carrier'}`
      : getImageAltText('mail');
  }
  if (status === 'processing' || status === 'awaiting_packing' || status === 'packaged') {
    return getImageAltText('calendarAngled');
  }

  if (status === 'shipped') {
    return getImageAltText('shipmentCourier');
  }

  if (status === 'delivered') {
    return getImageAltText('deliveryWoman');
  }

  return undefined;
};
